import React, { useEffect, useRef } from 'react';
import { Switch, Redirect, useHistory } from 'react-router-dom';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { RouteWithLayout, IFrameComponent, IFrameUnlayerEditor, IFrameUnlayerAttachmentEditor, IFrameGrapejsEditor } from './components';
import { Main as MainLayout, Minimal as MinimalLayout } from './layouts';
import { actions as UserActions } from './store/user';
import { actions as CampaignActions } from './store/campaign';
import { actions as RecipientQueryTemplateActions } from './store/recipientQueryTemplate';
import { actions as TemplateActions } from './store/template';
import { actions as AttachmentTemplateActions } from './store/attachmentTemplate';
import { actions as PrintTemplateActions } from './store/printTemplate';
import { actions as SurveyTemplateActions } from './store/surveyTemplate';
import { actions as ConditionQueryTemplateActions } from './store/conditionQueryTemplate';
import { actions as ListActions } from './store/lists';
import { actions as GuestInsightsActions } from './store/guestInsights';
import { actions as UnSubscribeActions } from './store/unSubscribe';
import { actions as EventReportActions } from './store/eventReports';
import { actions as BatchActions } from './store/batches';
import { actions as SurveyActions } from './store/surveys';
import { actions as SchemaActions } from './store/schema';
import { actions as EnrichedSchemaActions } from './store/enrichedSchema';
import { actions as SettingsActions } from './store/settings';
import { actions as tableAndToolbarActions } from './store/helpers/tableAndToolbar';
import { actions as uploadsActions } from './store/uploads';
import sessionHelper from 'helpers/sessionHelper';
import LicenseCheck from './LicenseCheck';
import PermissionCheck from 'PermissionsCheck.js';
import {
  getEnvVar,
  getDefaultLandingPathForTenant,
  isDashboardEnabledForTenant
} from 'helpers';
import { allowedIFrames } from 'store/helpers/helperFunctions';
import { excludePathForLanguageCode, resetFilterPaths } from 'constants.js';
import {
  Campaign as CampaignView,
  CampaignSettings as CampaignSettingsView,
  CampaignDetails as CampaignDetailsView,
  CampaignRecipients as CampaignRecipientsView,
  CampaignDesign as CampaignDesignView,
  CampaignSchedule as CampaignScheduleView,
  CreateEditCampaign as CreateEditCampaignView,
  Template as TemplateView,
  TemplateDetails as TemplateDetailsView,
  TemplateDesign as TemplateDesignView,
  PrintTemplate as PrintTemplateView,
  PrintTemplateDetails as PrintTemplateDetailsView,
  PrintTemplateDesign as PrintTemplateDesignView,
  AttachmentTemplate as AttachmentTemplateView,
  AttachmentTemplateDetails as AttachmentTemplateDetailsView,
  AttachmentTemplateDesign as AttachmentTemplateDesignView,
  SurveyTemplate as SurveyTemplateView,
  SurveyTemplateDetails as SurveyTemplateDetailsView,
  SurveyTemplateDesign as SurveyTemplateDesignView,
  RecipientQueries as RecipientQueriesView,
  RecipientQueryDetails as RecipientQueryDetailsView,
  RecipientQueryDesign as RecipientQueryDesignView,
  ConditionQueries as ConditionQueriesView,
  ConditionQueryDetails as ConditionQueryDetailsView,
  ConditionQueryDesign as ConditionQueryDesignView,
  ReportsSummary as ReportsSummaryView,
  ReportsBatches as ReportsBatchesView,
  UnSubscribe as UnSubscribeView,
  UnSubscribeEdit as UnSubscribeEditView,
  ReportsEvents as ReportsEventsView,
  EventDetails as EventDetailsView,
  MessageDetails as MessageDetailsView,
  ReportsCampaign as ReportsCampaignView,
  GuestInsights as GuestInsightsView,
  GuestInsightsDetails as GuestInsightsDetailsView,
  Uploads as UploadsView,
  // ReportsCampaignList as ReportsCampainListView,
  Surveys as SurveyView,
  SurveyDetails as SurveyDetailsView,
  SurveyDesign as SurveyDesignView,
  SurveyActions as SurveyActionsView,
  SurveyResults as SurveyResultsView,
  SurveyResultsResponse as SurveyResultsResponseView,
  Schema as SchemaView,
  SchemaDetails as SchemaDetailsView,
  SchemaDesign as SchemaDesignView,
  EnrichedSchema as EnrichedSchemaView,
  EnrichedSchemaDetails as EnrichedSchemaDetailsView,
  EnrichedSchemaDesign as EnrichedSchemaDesignView,
  Bootstrap as BootstrapView,
  DashboardPreview as DashboardPreviewView,
  Lists as ListsView,
  Subscribers as SubscribersView,
  SegmentedLists as SegmentedListsView,
  SegmentedListSettings as SegmentedListSettingsView,
  ImportStatus as ImportStatusView,
  SignIn as SignInView,
  ResetPassword as ResetPasswordView,
  SsoLogin as SsoLoginView,
  Forgot as ForgotView,
  NotFound as NotFoundView,
  Settings as SettingsView,
  LicenseNotFound as LicenseNotFoundView,
  TwoFactorAuthentication as TwoFactorAuthenticationView
} from './views';

import * as userMessagingDomains from './views/UserMessaging/userMessageDomains';

const { login } = UserActions;

const Routes = () => {
  const user = useSelector(store => store.user);
  const dispatch = useDispatch();
  const history = useHistory();
  const store = useStore();
  const { refreshSessionIfNeeded } = sessionHelper(store);
  const licenses = LicenseCheck();
  const {
    isLicenseLoading,
    canAccessBasicOrStandard,
    canAccessRecipientQueries,
    canAccessSurvey,
    canAccessListManagement,
    canAccessPrint
  } = licenses;
  const permissions = PermissionCheck();
  const { hasPermission, isAdminUser } = permissions;
  const currentTenantId =
    user.selectedTenant?.tenantId ??
    window.localStorage.getItem('GCM-tenantId');
  const isDashboardEnabled = isDashboardEnabledForTenant(currentTenantId);
  const defaultLandingPath = getDefaultLandingPathForTenant(currentTenantId);

  // Bootstrap guard: redirect to /bootstrap while the login sequence is resolving
  // so that conditional routes (e.g. /dashboard) are never evaluated before the
  // app's auth + license state is fully initialised.
  const hasSessionToken = !!(user.xToken || window.localStorage.getItem('GCM-xToken'));
  const isBootstrapping = !user.ready && (hasSessionToken || user.loading);
  const currentPath = history.location.pathname + history.location.search;
  const publicAuthPaths = ['/sign-in', '/ssoLogin', '/authentication', '/reset-password', '/not-found', '/not-authorized', '/bootstrap', '/forgot'];
  const isPublicAuthPath = publicAuthPaths.some(p => currentPath.startsWith(p));
  const shouldRedirectToBootstrap = isBootstrapping && !isPublicAuthPath;

  const receiveMessageFromParent = (event) => {  
    // To check if the message contains token and tenantId
    if (event.data.token && event.data.tenantId) {
      window.localStorage.setItem('GCM-parent-frame', event.origin);
    }
    if(allowedIFrames().length > 0 && event.data.token && event.data.tenantId && event.data.contextId) {
      // Handled authentication for iFrame
      window.localStorage.setItem('GCM-xToken', event.data.token);
      window.localStorage.setItem('GCM-tenantId', event.data.tenantId);
      window.localStorage.setItem('GCM-businessContextId', event.data.contextId);
      if (event.data.readOnly) {
        window.localStorage.setItem('GCM-readOnlyMode', event.data.readOnly);
      }
      if(event.data.languageCode) {
        window.localStorage.setItem('GCM-languageCode', event.data.languageCode);
      }
      dispatch(login());
      if(event.data.template) {
        dispatch(TemplateActions.setCurrentTemplate({
          ...event.data.template,
          hasMovedToNextStep: true
        }));
      } else if(event.data.attachmentTemplate) {
        dispatch(AttachmentTemplateActions.setCurrentAttachmentTemplate({
          ...event.data.attachmentTemplate,
          hasMovedToNextStep: true
        }))
      } else if(event.data.printTemplate) {
        dispatch(PrintTemplateActions.setCurrentPrintTemplate({
          ...event.data.printTemplate,
          hasMovedToNextStep: true
        }))
      }
    }
  };

  const resetStoreValues = (pathname) => {
    switch (pathname) {
      case 'campaigns':
        dispatch(CampaignActions.resetCampaignFilters());
        break;
      case 'recipient-queries':
        dispatch(RecipientQueryTemplateActions.resetRecipientQueryFilters());
        break;
      case 'templates':
        dispatch(TemplateActions.resetCommunicationFilters());
        break;
      case 'attachment-templates':
        dispatch(AttachmentTemplateActions.resetAttachmentFilters());
        break;
      case 'print-templates':
        dispatch(PrintTemplateActions.resetPrintFilters());
        break;
      case 'surveyResultResponse-templates':
        dispatch(SurveyTemplateActions.resetSurveyTemplateFilters());
        break;
      case 'condition-queries':
        dispatch(ConditionQueryTemplateActions.resetConditionQueryFilters());
        break;
      case 'lists':
        dispatch(ListActions.resetListFilters());
        break;
      case 'guest-insights':
        dispatch(GuestInsightsActions.resetGuestInsightFilters());
        break;
      // reset filter for campaign reports
      case 'unsubscribe':
        dispatch(UnSubscribeActions.resetUnsubscribeFilters());
        break;
      case 'events':
        dispatch(EventReportActions.resetEventReportFilters());
        break;
      case 'batches':
        dispatch(BatchActions.resetBatchFilters());
        break;
      case 'surveys':
        dispatch(SurveyActions.resetSurveyFilters());
        break;
      case 'schema':
        dispatch(SchemaActions.resetSchemaFilters());
        break;
      case 'enriched-schema':
        dispatch(EnrichedSchemaActions.resetEnrichedSchemaFilters());
        break;
      case 'reports':
        dispatch(tableAndToolbarActions.resetFilters());
        break;
      case 'uploads':
        dispatch(uploadsActions.resetUploadFilters());
        break;
    }
  }

  let previousPathnameRef = useRef(null);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return history.listen((location) => {
      let pathname = location.pathname.split("/")[1];
      const previousPathname = previousPathnameRef.current;
      previousPathnameRef.current = pathname;
      if (previousPathname !== pathname && resetFilterPaths.some(path => path === location.pathname)) {
        resetStoreValues(pathname);
        resetStoreValues(previousPathname);
      }
      if (history.location.pathname) {
        const storeState = store.getState();
        const availableLanguagesStore = storeState.settings.availableLanguages;
        const settingsMetaDataStore = storeState.settings.settingsMetaData.data;
        // To check if any value in excludePathForLanguageCode is present in current route path
        const isPresent = excludePathForLanguageCode.some(path => history.location.pathname.includes(path));
        if (!isPresent && availableLanguagesStore?.length > 0 && settingsMetaDataStore?.languageCode) {
          dispatch(SettingsActions.updateTemplateLanguagePreference(availableLanguagesStore.filter(language => language.languageCode === settingsMetaDataStore?.languageCode)[0]));
        }
      }
      refreshSessionIfNeeded();
    });
  }, [history]);

  useEffect(() => {
    if((window.self !== window.top)) { //Condition for finding iFrame
      window.addEventListener('message', receiveMessageFromParent);
    } else {
      window.localStorage.removeItem('GCM-parent-frame');
      window.localStorage.removeItem('GCM-readOnlyMode');
      dispatch(login());
    }
  }, []);

  return (
    (window.self !== window.top) //Condition for finding iFrame
      ? <Switch>
        <RouteWithLayout
          component={IFrameComponent}
          exact
          path="/editor/:editor/:id?"
          />
        <RouteWithLayout
          component={IFrameUnlayerEditor}
          exact
          path="/unlayerEditor/:templateUuid?"
          />
        <RouteWithLayout
          component={IFrameUnlayerAttachmentEditor}
          exact
          path="/unlayerAttachmentEditor/:templateUuid?"
          />
        <RouteWithLayout
          component={IFrameGrapejsEditor}
          exact
          path="/grapejsEditor/:templateId?"
          />
        <RouteWithLayout
          component={NotFoundView}
          exact
          path="/not-found"
          />
        <RouteWithLayout
          component={LicenseNotFoundView}
          exact
          path="/not-authorized"
          />
        <Redirect to="/not-found" />
      </Switch>
      : <Switch>
        {user.xToken && <Redirect exact from="/" to={defaultLandingPath} />}
        {!user.xToken && <Redirect exact from="/" to="/sign-in" />}
        {user.hasPasswordExpired && (
          <Redirect exact from="/sign-in" to="/reset-password" />
        )}
        {/* Bootstrap guard: hold any authenticated navigation until login + licenses are fully resolved */}
        {shouldRedirectToBootstrap && <Redirect to={{ pathname: '/bootstrap', state: { returnTo: currentPath } }} />}
        {/* lisenceFeatures check */}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/campaigns" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/campaigns/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/settings" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/settings/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/templates" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/templates/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessPrint && (
          <Redirect exact from="/print-templates" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessPrint && (
          <Redirect exact from="/print-templates/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/attachment-templates/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/attachment-templates" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/reports" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/reports/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/events" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/events/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/messages/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessPrint && (
          <Redirect exact from="/batches" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessPrint && (
          <Redirect exact from="/batches/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessRecipientQueries && (
          <Redirect exact from="/recipient-queries" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessRecipientQueries && (
          <Redirect exact from="/recipient-queries/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/condition-queries" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessBasicOrStandard && (
          <Redirect exact from="/condition-queries/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessSurvey && (
          <Redirect exact from="/surveys" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessSurvey && (
          <Redirect exact from="/surveys/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessListManagement && (
          <Redirect exact from="/lists" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessListManagement && (
          <Redirect exact from="/lists/*" to="/not-authorized" />
        )}
        {!isLicenseLoading && !canAccessSurvey && (
          <Redirect
            exact
            from="/surveyResultResponse-templates/*"
            to="/not-authorized"
          />
        )}
        {!isLicenseLoading && !canAccessSurvey && (
          <Redirect
            exact
            from="/surveyResultResponse-templates"
            to="/not-authorized"
          />
        )}
        {!isLicenseLoading &&
          user.xToken &&
          (!hasPermission('Rguest_writeSchema') || !isAdminUser()) && (
            <Redirect exact from="/schema" to="/not-authorized" />
          )}
        {!isLicenseLoading &&
          user.xToken &&
          (!hasPermission('Rguest_writeSchema') || !isAdminUser()) && (
            <Redirect exact from="/schema/*" to="/not-authorized" />
          )}
        {!isLicenseLoading &&
          user.xToken &&
          (!hasPermission('Rguest_writeSchema') || !isAdminUser()) && (
            <Redirect exact from="/enriched-schema" to="/not-authorized" />
          )}
        {!isLicenseLoading &&
          user.xToken &&
          (!hasPermission('Rguest_writeSchema') || !isAdminUser()) && (
            <Redirect exact from="/enriched-schema/*" to="/not-authorized" />
          )}
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SettingsView}
          exact
          userMessageDomain={userMessagingDomains.SETTINGS}
          layout={MainLayout}
          path="/settings"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CampaignView}
          exact
          userMessageDomain={userMessagingDomains.CAMPAIGNS}
          layout={MainLayout}
          path="/campaigns"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CreateEditCampaignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CAMPAIGN}
          path="/campaigns/edit/:campaignUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CampaignSettingsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CAMPAIGN}
          path="/campaigns/settings"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CampaignDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CAMPAIGN}
          path="/campaigns/details/:campaignUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CampaignRecipientsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CAMPAIGN}
          path="/campaigns/recipients/:campaignUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CampaignDesignView}
          exact
          layout={MainLayout}
          path="/campaigns/design/:campaignUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={CampaignScheduleView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CAMPAIGN}
          path="/campaigns/schedule/:campaignUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={TemplateView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.TEMPLATES}
          path="/templates"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={TemplateDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.TEMPLATE}
          path="/templates/details/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={TemplateDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.TEMPLATE}
          path="/templates/design/:templateUuid?"
        />
        <RouteWithLayout
          key="print-list"
          isLoading={isLicenseLoading}
          component={PrintTemplateView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.TEMPLATES}
          path="/print-templates"
        />
        <RouteWithLayout
          key="print-detail"
          isLoading={isLicenseLoading}
          component={PrintTemplateDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.TEMPLATE}
          path="/print-templates/details/:templateId?"
        />
        <RouteWithLayout
          key="print-design"
          isLoading={isLicenseLoading}
          component={PrintTemplateDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.TEMPLATE}
          path="/print-templates/design/:templateId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={AttachmentTemplateView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.ATTACHMENT_TEMPLATES}
          path="/attachment-templates"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={UploadsView}
          exact
          layout={MainLayout}
          path="/uploads"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={AttachmentTemplateDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.ATTACHMENT_TEMPLATE}
          path="/attachment-templates/details/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={AttachmentTemplateDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.ATTACHMENT_TEMPLATE}
          path="/attachment-templates/design/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyTemplateView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEY_TEMPLATES}
          path="/surveyResultResponse-templates"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyTemplateDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEY_TEMPLATE}
          path="/surveyResultResponse-templates/details/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyTemplateDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEY_TEMPLATE}
          path="/surveyResultResponse-templates/design/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={RecipientQueriesView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.RECIPIENTQUERIES}
          path="/recipient-queries"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={RecipientQueryDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.RECIPIENTQUERY}
          path="/recipient-queries/details/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={RecipientQueryDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.RECIPIENTQUERY}
          path="/recipient-queries/design/:templateUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ConditionQueriesView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CONDITIONQUERIES}
          path="/condition-queries"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ConditionQueryDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CONDITIONQUERIES}
          path="/condition-queries/details/:queryId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ConditionQueryDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.CONDITIONQUERIES}
          path="/condition-queries/design/:queryId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ReportsEventsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.EVENTS}
          path="/events"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={EventDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.EVENTS}
          path="/events/details"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={MessageDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.EVENTS}
          path="/messages/details/:messageUuid?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={UnSubscribeView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.BATCHES}
          path="/unsubscribe"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={UnSubscribeEditView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.BATCHES}
          path="/unsubscribe/Edit"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ReportsBatchesView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.BATCHES}
          path="/batches"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ReportsSummaryView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SUMMARY}
          path="/reports"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ReportsCampaignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SUMMARY}
          path="/reports/campaigns/:campaignUuid/:type/details"
          key="campaign-details"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={ReportsCampaignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SUMMARY}
          path="/reports/campaigns/:campaignUuid/:type/activity/:providerMetric"
          key="campaign-activity"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEYS}
          path="/surveys"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEYS}
          path="/surveys/details/:surveyId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEYS}
          path="/surveys/design/:surveyId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyActionsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEYS}
          path="/surveys/actions/:surveyId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyResultsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEYS}
          path="/surveys/results/:surveyId/:surveyHash"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SurveyResultsResponseView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SURVEYS}
          path="/surveys/results/:surveyId/:surveyHash/response/:eventIdParam?/:campaignUuidParam?"
        />
        {isDashboardEnabled && (
          <RouteWithLayout
            isLoading={isLicenseLoading}
            component={DashboardPreviewView}
            exact
            layout={MainLayout}
            path="/dashboard"
          />
        )}
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SchemaView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SCHEMA}
          path="/schema"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SchemaDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SCHEMA}
          path="/schema/details"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={SchemaDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.SCHEMA}
          path="/schema/design/:schemaId?"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={EnrichedSchemaView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.ENRICHED_SCHEMA}
          path="/enriched-schema"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={EnrichedSchemaDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.ENRICHED_SCHEMA}
          path="/enriched-schema/details"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={EnrichedSchemaDesignView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.ENRICHED_SCHEMA}
          path="/enriched-schema/design/:enrichedSchemaId?"
        />
        {getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' && [
          <RouteWithLayout
            isLoading={isLicenseLoading}
            component={ListsView}
            exact
            layout={MainLayout}
            userMessageDomain={userMessagingDomains.LISTS}
            path="/lists"
          />,
          <RouteWithLayout
            isLoading={isLicenseLoading}
            component={SubscribersView}
            exact
            layout={MainLayout}
            userMessageDomain={userMessagingDomains.LISTS}
            path="/lists/:listId/subscribers"
          />,
          <RouteWithLayout
            isLoading={isLicenseLoading}
            component={SegmentedListsView}
            exact
            layout={MainLayout}
            userMessageDomain={userMessagingDomains.LISTS}
            path="/lists/:listId/segmented-list"
          />,
          <RouteWithLayout
            isLoading={isLicenseLoading}
            component={SegmentedListSettingsView}
            exact
            layout={MainLayout}
            userMessageDomain={userMessagingDomains.LISTS}
            path="/lists/:listId/segmented-list/:segmentedListId?/settings"
          />,
          <RouteWithLayout
            isLoading={isLicenseLoading}
            component={ImportStatusView}
            exact
            layout={MainLayout}
            userMessageDomain={userMessagingDomains.LISTS}
            path="/lists/:listId/import-status"
          />
        ]}

        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={GuestInsightsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.GUEST_INSIGHTS}
          path="/guest-insights"
        />
        <RouteWithLayout
          isLoading={isLicenseLoading}
          component={GuestInsightsDetailsView}
          exact
          layout={MainLayout}
          userMessageDomain={userMessagingDomains.GUEST_INSIGHTS}
          path="/guest-insights/details/:guestProfileUuid?"
        />
        <RouteWithLayout component={BootstrapView} exact path="/bootstrap" />
        <RouteWithLayout component={SignInView} exact path="/sign-in" />
        <RouteWithLayout component={SsoLoginView} exact path="/ssoLogin" />
        <RouteWithLayout
          component={SsoLoginView}
          exact
          path="/ssoLogin/tenants/:tenantId/context/:contextId"
        />
        <RouteWithLayout
          component={ResetPasswordView}
          exact
          path="/reset-password"
        />
        <RouteWithLayout
          component={ForgotView}
          exact
          path="/forgot/:forgotDetail"
        />
        <RouteWithLayout
          component={TwoFactorAuthenticationView}
          exact
          path="/authentication"
        />
        <RouteWithLayout
          component={NotFoundView}
          exact
          layout={MinimalLayout}
          path="/not-found"
        />
        <RouteWithLayout
          component={LicenseNotFoundView}
          exact
          layout={user.licenseFailed ? MainLayout : MinimalLayout}
          path="/not-authorized"
        />
        <Redirect to="/not-found" />
      </Switch>
  );
};

export default Routes;
